import { useMemo } from 'react';
import { parseMentions } from '@/utils/mentionUtils';

export interface MentionReference {
  messageId: string;
  messageText: string;
  senderUsername: string;
  senderId: string;
  timestamp: string;
  mentionedUsers: string[];
}

interface Message {
  message_id: string;
  user_id: string;
  message: string;
  created_at: string;
  user_profile?: {
    username: string;
    first_name?: string;
    last_name?: string;
  };
}

export const useMentionNavigation = (
  messages: Message[],
  currentUsername?: string
) => {
  const mentionReferences = useMemo(() => {
    if (!currentUsername || !messages || messages.length === 0) {
      return [];
    }

    const mentions: MentionReference[] = [];

    messages.forEach((message) => {
      if (!message.message) return;

      // Parse mentions from the message
      const mentionedUsers = parseMentions(message.message);
      
      // Check if current user is mentioned (case-insensitive)
      const isCurrentUserMentioned = mentionedUsers.some(
        (mentionedUser) => mentionedUser.toLowerCase() === currentUsername.toLowerCase()
      );

      if (isCurrentUserMentioned) {
        mentions.push({
          messageId: message.message_id,
          messageText: message.message,
          senderUsername: message.user_profile?.username || `User-${message.user_id?.slice(0, 8)}`,
          senderId: message.user_id,
          timestamp: message.created_at,
          mentionedUsers
        });
      }
    });

    // Sort by timestamp (newest first)
    return mentions.sort((a, b) => 
      new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime()
    );
  }, [messages, currentUsername]);

  return {
    mentions: mentionReferences,
    mentionCount: mentionReferences.length,
    hasMentions: mentionReferences.length > 0
  };
};
